// JogosConfigView — admin gerencia área de Jogos:
//   - Toggle global da área (areaEnabled)
//   - Por jogo: enabled, isNew (badge "Novo"), name, desc, tag
//
// Catálogo de jogos é fixo no servidor (apenas Trilha hoje). Admin edita os
// metadados e ligas o que aparece pro aluno.

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  Gamepad2,
  ImageOff,
  Loader2,
  Save,
  Sparkles,
  Trash2,
  Upload
} from "lucide-react";
import { Skeleton } from "@app/ui/components/skeleton";
import { api, ApiError, type GameRec, type GamesConfig } from "./api";
import { openAlert } from "./ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

const data = signal<GamesConfig | null>(null);
const phase = signal<"loading" | "ready" | "error">("loading");
const errMsg = signal<string | null>(null);
const saving = signal<boolean>(false);
const savedAt = signal<number | null>(null);

async function load() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    data.value = await api.getGamesConfig();
    phase.value = "ready";
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = friendlyError(err);
    phase.value = "error";
  }
}

export function JogosConfigView() {
  useEffect(() => {
    load();
  }, []);
  const ph = phase.value;
  if (ph === "loading") return <JogosLoading />;
  if (ph === "error") return <JogosError msg={errMsg.value} />;
  return <JogosEditor initial={data.value!} />;
}

function JogosLoading() {
  return (
    <div class="space-y-3">
      <Skeleton className="h-14 rounded-md" />
      <Skeleton className="h-48 rounded-md" />
    </div>
  );
}

function JogosError({ msg }: { msg: string | null }) {
  return (
    <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3">
      <div class="flex items-start gap-2">
        <AlertCircle className="mt-0.5 size-5 shrink-0 text-destructive" />
        <div>
          <h3 class="text-sm font-bold">Não consegui carregar a config de jogos</h3>
          {msg && <p class="text-xs text-muted-foreground">{msg}</p>}
          <button
            type="button"
            onClick={() => load()}
            class="mt-2 rounded-md border border-border bg-background px-3 py-1 text-xs font-semibold hover:bg-accent"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    </div>
  );
}

function JogosEditor({ initial }: { initial: GamesConfig }) {
  const [areaEnabled, setAreaEnabled] = useState(initial.areaEnabled);
  const [games, setGames] = useState<Record<string, GameRec>>(initial.games);

  function patchGame(id: string, partial: Partial<GameRec>) {
    setGames({ ...games, [id]: { ...games[id], ...partial } });
  }

  async function handleSave() {
    saving.value = true;
    try {
      const saved = await api.saveGamesConfig({ areaEnabled, games });
      data.value = saved;
      savedAt.value = saved.savedAt;
      setAreaEnabled(saved.areaEnabled);
      setGames(saved.games);
    } catch (err) {
      void openAlert({
        title: "Erro",
        message:
          err instanceof Error ? `Falha ao salvar: ${err.message}` : "Falha ao salvar."
      });
    } finally {
      saving.value = false;
    }
  }

  return (
    <div class="space-y-4">
      {/* Status bar */}
      <div class="flex flex-wrap items-center gap-3 rounded-md border border-border bg-card/40 px-4 py-3">
        <button
          type="button"
          onClick={() => setAreaEnabled(!areaEnabled)}
          class={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-semibold transition-colors ${
            areaEnabled
              ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
              : "border-border bg-background text-muted-foreground hover:bg-accent"
          }`}
        >
          <span
            class={`size-2.5 rounded-full ${areaEnabled ? "bg-emerald-500" : "bg-muted-foreground/40"}`}
          />
          {areaEnabled ? "Área de Jogos ativa" : "Área de Jogos desativada"}
        </button>
        <span class="hidden text-xs text-muted-foreground sm:inline">
          {areaEnabled
            ? "Aluno vê o menu de Jogos e os jogos habilitados abaixo."
            : "Menu de Jogos fica oculto para todos — independente dos toggles abaixo."}
        </span>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving.value}
          class="ml-auto inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-1.5 text-sm font-bold text-white shadow-sm transition-colors hover:brightness-110 disabled:opacity-60"
        >
          {saving.value ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Save width="1.1em" height="1.1em" />
          )}
          Salvar
        </button>
      </div>

      {savedAt.value && (
        <div class="inline-flex items-center gap-1.5 rounded-md bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-700 dark:text-emerald-300">
          <CheckCircle2 width="1.1em" height="1.1em" />
          Salvo agora há pouco
        </div>
      )}

      {/* Cards por jogo do catálogo */}
      <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {initial.catalog.map((meta) => {
          const rec = games[meta.id];
          if (!rec) return null;
          return (
            <article
              key={meta.id}
              class={`space-y-3 rounded-md border bg-card/40 p-4 transition-opacity ${
                rec.enabled ? "border-border" : "border-border opacity-70"
              }`}
            >
              <header class="flex items-start gap-3">
                <GameThumb
                  src={rec.thumbUrl}
                  alt={rec.name || meta.defaultName}
                />
                <div class="min-w-0 flex-1">
                  <h3 class="flex items-center gap-2 text-base font-bold">
                    {rec.name || meta.defaultName}
                    {rec.isNew && (
                      <span class="inline-flex items-center gap-1 rounded-md bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300">
                        <Sparkles width="10" height="10" />
                        Novo
                      </span>
                    )}
                  </h3>
                  <p class="text-[11px] font-mono text-muted-foreground/80">
                    {meta.path}
                  </p>
                </div>
                <a
                  href={meta.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Abrir jogo em nova aba"
                  class="rounded-md border border-border bg-background px-2 py-1 hover:bg-accent"
                >
                  <ExternalLink width="14" height="14" />
                </a>
              </header>

              {/* Toggles */}
              <div class="flex flex-wrap gap-2">
                <ToggleChip
                  label={rec.enabled ? "Ativo" : "Inativo"}
                  active={rec.enabled}
                  color="emerald"
                  onClick={() => patchGame(meta.id, { enabled: !rec.enabled })}
                />
                <ToggleChip
                  label="Marcar como Novo"
                  active={rec.isNew}
                  color="amber"
                  onClick={() => patchGame(meta.id, { isNew: !rec.isNew })}
                />
              </div>

              {/* Edição de campos */}
              <div class="space-y-2">
                <LabeledField
                  label="Nome"
                  value={rec.name}
                  placeholder={meta.defaultName}
                  maxLength={40}
                  onChange={(v) => patchGame(meta.id, { name: v })}
                />
                <LabeledField
                  label="Tag"
                  value={rec.tag}
                  placeholder={meta.defaultTag}
                  maxLength={24}
                  onChange={(v) => patchGame(meta.id, { tag: v })}
                />
                <LabeledArea
                  label="Descrição"
                  value={rec.desc}
                  placeholder={meta.defaultDesc}
                  maxLength={240}
                  onChange={(v) => patchGame(meta.id, { desc: v })}
                />
                <ThumbUrlField
                  value={rec.thumbUrl ?? ""}
                  onChange={(v) =>
                    patchGame(meta.id, { thumbUrl: v.trim() ? v : null })
                  }
                />
              </div>
            </article>
          );
        })}
      </div>

      {initial.savedBy && initial.savedAt && (
        <p class="text-xs text-muted-foreground">
          Última edição:{" "}
          <strong class="font-semibold text-foreground">{initial.savedBy}</strong>{" "}
          · {new Date(initial.savedAt).toLocaleString("pt-BR")}
        </p>
      )}
    </div>
  );
}

function ToggleChip({
  label,
  active,
  color,
  onClick
}: {
  label: string;
  active: boolean;
  color: "emerald" | "amber";
  onClick: () => void;
}) {
  const onCls =
    color === "amber"
      ? "border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-300"
      : "border-emerald-500/50 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300";
  return (
    <button
      type="button"
      onClick={onClick}
      class={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-semibold transition-colors ${
        active ? onCls : "border-border bg-background/50 text-muted-foreground hover:bg-accent"
      }`}
    >
      <span
        class={`size-2 rounded-full ${active ? (color === "amber" ? "bg-amber-500" : "bg-emerald-500") : "bg-muted-foreground/30"}`}
      />
      {label}
    </button>
  );
}

function LabeledField({
  label,
  value,
  placeholder,
  maxLength,
  onChange
}: {
  label: string;
  value: string;
  placeholder?: string;
  maxLength?: number;
  onChange: (v: string) => void;
}) {
  return (
    <label class="block">
      <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        maxLength={maxLength}
        onInput={(e) => onChange((e.target as HTMLInputElement).value)}
        class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
      />
    </label>
  );
}

function LabeledArea({
  label,
  value,
  placeholder,
  maxLength,
  onChange
}: {
  label: string;
  value: string;
  placeholder?: string;
  maxLength?: number;
  onChange: (v: string) => void;
}) {
  return (
    <label class="block">
      <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <textarea
        value={value}
        placeholder={placeholder}
        maxLength={maxLength}
        rows={3}
        onInput={(e) => onChange((e.target as HTMLTextAreaElement).value)}
        class="w-full resize-y rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
      />
    </label>
  );
}

// Preview da miniatura no header do card.
// Quando thumbUrl é null, mostra o ícone genérico Gamepad2 com cor de
// brand. Quando tem URL, renderiza <img> com fallback pro ícone se a
// imagem falhar.
function GameThumb({ src, alt }: { src: string | null; alt: string }) {
  const [errored, setErrored] = useState(false);
  if (src && !errored) {
    return (
      <span class="grid size-12 shrink-0 place-items-center overflow-hidden rounded-md bg-emerald-500/10 ring-1 ring-emerald-500/20">
        <img
          src={src}
          alt={alt}
          loading="lazy"
          onError={() => setErrored(true)}
          class="size-full object-cover"
        />
      </span>
    );
  }
  return (
    <span class="grid size-12 shrink-0 place-items-center rounded-md bg-emerald-500/10 text-emerald-600 ring-1 ring-emerald-500/20 dark:text-emerald-400">
      <Gamepad2 className="size-6" />
    </span>
  );
}

// Campo de miniatura: aceita upload de arquivo (resize client-side pra
// 256×256 webp ~10KB → data URL inline) OU URL externa (http/https/path).
// Sem storage backend dedicado — a data URL fica direto no jsonb da config.
function ThumbUrlField({
  value,
  onChange
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const trimmed = value.trim();
  const isDataUrl = /^data:image\//i.test(trimmed);
  const looksValid =
    isDataUrl || /^(https?:\/\/|\/)/i.test(trimmed) || !trimmed;

  async function pickFile(e: Event) {
    const input = e.target as HTMLInputElement;
    const f = input.files?.[0];
    input.value = ""; // permite re-selecionar mesmo arquivo
    if (!f) return;
    if (!/^image\//.test(f.type)) {
      setErr("Selecione um arquivo de imagem.");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      setErr("Arquivo > 5MB. Use uma imagem menor.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const dataUrl = await resizeToDataUrl(f, 256, 0.75);
      if (dataUrl.length > 48_000) {
        setErr("Imagem grande mesmo após compressão. Tente uma menor.");
        return;
      }
      onChange(dataUrl);
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : "Falha ao processar imagem.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <label class="block">
      <span class="mb-1 flex items-center justify-between gap-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        <span>Miniatura</span>
        {trimmed && (
          <button
            type="button"
            onClick={() => onChange("")}
            class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          >
            <Trash2 className="size-3" />
            Remover
          </button>
        )}
      </span>
      <div class="flex gap-1.5">
        <input
          type="text"
          value={isDataUrl ? `[imagem (${(trimmed.length / 1024).toFixed(0)} KB)]` : value}
          placeholder="https://… ou /games/.../thumb.png"
          maxLength={50_000}
          disabled={isDataUrl}
          onInput={(e) => onChange((e.target as HTMLInputElement).value)}
          class={`min-w-0 flex-1 rounded-md border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary ${
            trimmed && !looksValid ? "border-destructive/50" : "border-border"
          } ${isDataUrl ? "text-muted-foreground italic" : ""}`}
        />
        <label
          class={`inline-flex shrink-0 cursor-pointer items-center gap-1 rounded-md border border-border bg-background px-2 py-1.5 text-xs font-semibold hover:bg-accent ${
            busy ? "opacity-60" : ""
          }`}
          title="Subir arquivo (redimensionado pra 256×256 webp)"
        >
          {busy ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <Upload className="size-3.5" />
          )}
          Upload
          <input
            type="file"
            accept="image/*"
            class="hidden"
            disabled={busy}
            onChange={pickFile}
          />
        </label>
      </div>
      {err && (
        <p class="mt-1 flex items-center gap-1 text-[10px] text-destructive">
          <ImageOff className="size-3" />
          {err}
        </p>
      )}
      {trimmed && !looksValid && !err && (
        <p class="mt-1 flex items-center gap-1 text-[10px] text-destructive">
          <ImageOff className="size-3" />
          URL deve começar com http(s)://, / ou data:image/
        </p>
      )}
    </label>
  );
}

// Redimensiona arquivo de imagem pra `target × target` (mantém aspect via
// cover), comprime em webp com `quality`. Retorna data URL pronta pra ser
// usada em <img src> ou armazenada no jsonb. Cap ~50KB controlado pelo caller.
async function resizeToDataUrl(
  file: File,
  target: number,
  quality: number
): Promise<string> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = target;
        canvas.height = target;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Canvas 2D indisponível"));
          return;
        }
        // Cover: corta pra preencher quadrado sem distorção
        const ratio = Math.max(target / img.width, target / img.height);
        const w = img.width * ratio;
        const h = img.height * ratio;
        const dx = (target - w) / 2;
        const dy = (target - h) / 2;
        ctx.fillStyle = "#000";
        ctx.fillRect(0, 0, target, target);
        ctx.drawImage(img, dx, dy, w, h);
        const dataUrl = canvas.toDataURL("image/webp", quality);
        URL.revokeObjectURL(url);
        resolve(dataUrl);
      } catch (e) {
        URL.revokeObjectURL(url);
        reject(e);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Falha ao carregar imagem"));
    };
    img.src = url;
  });
}
